import { Switch, Typography } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AddProjectCustomerButton } from 'pricing/components';
import { useGetProjectCustomers, useProjectCustomerActions } from 'pricing/hooks';
import { IPricingProfile } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  projectId: number;
  projectPricingProfileId: number;
}

export const ProjectCustomerList = ({
  pricingProfile,
  projectId,
  projectPricingProfileId,
}: IProps) => {
  const { t } = useTranslation();

  const { records, isLoading, isAllCustomers, loadingPercentage, currentPage } =
    useGetProjectCustomers({
      pricingProfileId: pricingProfile.id,
      projectId,
    });

  const { toggleAllCustomers } = useProjectCustomerActions({
    notificationPosition: NotificationPosition.TOP,
  });

  const columnDefs = useMemo(
    () => getColumnDef({ pricingProfileId: pricingProfile.id, projectId }),
    [pricingProfile.id, projectId]
  );

  const onToggleAllCustomers = useCallback(async () => {
    await toggleAllCustomers({
      projectPricingProfile: {
        applyToAllCustomers: isAllCustomers ? 0 : 1,
        id: projectPricingProfileId,
      },
    });
  }, [isAllCustomers, projectPricingProfileId, toggleAllCustomers]);

  const actionButtons = useMemo(
    () => [
      <AddProjectCustomerButton
        key="addProjectCustomerButton"
        pricingProfileId={pricingProfile.id}
        projectId={projectId}
        disabled={isAllCustomers}
      />,
    ],
    [pricingProfile.id, projectId, isAllCustomers]
  );

  const rightActionButtons = useMemo(
    () => [
      <Typography
        key="applyToAllCustomersToggle"
        component="label"
        endDecorator={<Switch checked={isAllCustomers} onChange={onToggleAllCustomers} />}
      >
        {t('labelApplyToAllCustomers')}
      </Typography>,
    ],
    [isAllCustomers, onToggleAllCustomers, t]
  );

  return (
    <AgGridClientTableProvider
      tableId="projectCustomersTable"
      entityType="ProjectCustomer"
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      hasRowSelection={false}
    >
      <AgGridTable actionButtons={actionButtons} rightActionButtons={rightActionButtons} />
    </AgGridClientTableProvider>
  );
};
